import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TbShoppingCart } from 'react-icons/tb';
import { HiOutlineUser } from 'react-icons/hi';
import ThiswayLogo from '../../../assets/logo/logo-black.svg';
import useAuth from '../../../utils/hooks/useAuth';
import useLoginModal from '../../../utils/hooks/useLoginModal';
import useCondensedHeader from '../../../utils/hooks/useCondensedHeader';
import useCheckoutLocked from '../../../utils/hooks/useCheckoutLocked';
import UserDropdown from './UserDropdown';
import LoginModal from './LoginModal';

/**
 * New-UI global header. Matches the rest of the redesign:
 * - Compact glass bar that picks up a subtle shadow on scroll.
 * - Icon buttons styled with surface-2 / line tokens.
 * - Cart action rendered as the accent-branded primary button so
 *   adding items has a clear visual anchor.
 */
const Header = ({ onCartClick }) => {
  const { authenticated } = useAuth();
  const { isOpen: isLoginModalOpen, openLogin, closeLogin } = useLoginModal();
  const { isActive: condensedActive } = useCondensedHeader();
  const currentStore = useSelector((state) => state.base.common.currentStore);
  const basket = useSelector((state) => state.basket.basket);
  const basketItemQty = basket.items.reduce((a, b) => a + b.qty, 0);
  const [scrolled, setScrolled] = useState(false);

  // Hide the cart button once the order is locked in (payment step): a created
  // payment intent must not be edited via the cart sidebar. On the earlier
  // review step the cart stays available so the customer can still edit.
  const { isLocked: checkoutLocked } = useCheckoutLocked();
  const showCart = !checkoutLocked;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      className="sticky top-0 z-50"
      style={{
        paddingTop: 'var(--sat)',
        background: 'var(--ui-bg)',
        borderBottom: `1px solid ${
          scrolled && !condensedActive ? 'var(--ui-line)' : 'transparent'
        }`,
        opacity: condensedActive ? 0 : 1,
        visibility: condensedActive ? 'hidden' : 'visible',
        pointerEvents: condensedActive ? 'none' : 'auto',
        transition: condensedActive
          ? 'opacity 0.18s cubic-bezier(0.2, 0.8, 0.2, 1), visibility 0s 0.18s, background 0.2s, border-color 0.2s, backdrop-filter 0.2s'
          : 'opacity 0.2s cubic-bezier(0.2, 0.8, 0.2, 1) 0.08s, visibility 0s, background 0.2s, border-color 0.2s, backdrop-filter 0.2s',
        fontFamily: 'Geist, system-ui, sans-serif',
      }}
    >
      <div
        className="w-full max-w-5xl mx-auto flex items-center justify-between px-4 sm:px-6"
        style={{ height: '58px' }}
      >
        <Link
          to={`/${currentStore?.slug || 'home'}`}
          className="inline-flex items-center"
          style={{ textDecoration: 'none' }}
        >
          <img src={ThiswayLogo} alt="thisway" style={{ height: '22px' }} />
        </Link>

        <div className="flex items-center gap-2">
          {!authenticated ? (
            <button
              type="button"
              onClick={openLogin}
              className="inline-flex items-center gap-2 transition-colors"
              style={{
                height: '40px',
                padding: '0 14px',
                borderRadius: '10px',
                background: 'transparent',
                border: '1px solid var(--ui-line-2)',
                color: 'var(--ui-ink)',
                fontFamily: 'inherit',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <HiOutlineUser size={15} />
              Login
            </button>
          ) : (
            <UserDropdown />
          )}

          {showCart && (
            <button
              type="button"
              onClick={onCartClick}
              aria-label={`Cart · ${basketItemQty} item${
                basketItemQty !== 1 ? 's' : ''
              }`}
              className="relative grid place-items-center transition-transform"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'var(--ui-ink)',
                color: '#FFFFFF',
                border: 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <TbShoppingCart size={18} />
              {basketItemQty > 0 && (
                <span
                  className="absolute font-extrabold grid place-items-center"
                  style={{
                    top: '-6px',
                    right: '-6px',
                    minWidth: '20px',
                    height: '20px',
                    padding: '0 6px',
                    borderRadius: '100px',
                    background: 'var(--accent, var(--site-color))',
                    color: 'var(--accent-fg, #FFFFFF)',
                    fontSize: '10.5px',
                    border: '2px solid var(--ui-bg, #FAFAF9)',
                    fontFamily: 'Geist Mono, monospace',
                  }}
                >
                  {basketItemQty}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Login modal (uses its own new/legacy branch internally) */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLogin} />
    </div>
  );
};

export default Header;
